import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  MainContainer, StyledCard, FormGrid, StyledFormGroup,
  CardTitle, TooltipWrapper, InfoIcon,
  DropdownList, DropdownItem, DashButton, NcmTag
} from './style';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { ScenarioCard } from './ScenarioCard';

export interface TaxCodeOption { code: string; description: string }
export interface TaxCodesData {
  cstIcms: TaxCodeOption[];
  csosn: TaxCodeOption[];
  cstPisCofins: TaxCodeOption[];
  cstIbsCbs: (TaxCodeOption & { id: number })[];
}
export interface ClassTribOption { code: string; description: string; cstIbsCbsId: number | null }

interface LayoutContext {
  setHandleChildSubmit: (fn: () => void) => void;
}

interface Company {
  id: number;
  companyName: string;
  cnpj: string;
  stateId: number;
  state: {
    id: number;
    name: string;
  };
}

export interface TaxScenario {
  id: number;
  cstICMS: string;
  aliquotaICMS: number;
  baseDeCalculo: number;
  cstIBS_CBS: string;
  classificacaoTributaria: string;
  aliquotaCBS: number;
  aliquotaIBS: number;
  diferencialAliquota: boolean;
  substituicaoTributaria: boolean;
  stateIds: number[];
  tempStateNames?: string;
  regions: string[];
  showStateInput: boolean;
  applyToSameState: boolean;
}

const CST_IPI_OPTIONS = [
  { value: '00', label: '00 — Entrada com recuperação de crédito' },
  { value: '01', label: '01 — Entrada tributada com alíquota zero' },
  { value: '02', label: '02 — Entrada isenta' },
  { value: '03', label: '03 — Entrada não-tributada' },
  { value: '04', label: '04 — Entrada imune' },
  { value: '05', label: '05 — Entrada com suspensão' },
  { value: '49', label: '49 — Outras entradas' },
  { value: '50', label: '50 — Saída tributada' },
];

export const DEFAULT_SCENARIO: Omit<TaxScenario, 'id'> = {
  aliquotaICMS: 7,
  aliquotaCBS: 0,
  aliquotaIBS: 0,
  baseDeCalculo: 100,
  cstICMS: '010',
  cstIBS_CBS: '01',
  classificacaoTributaria: '',
  diferencialAliquota: false,
  stateIds: [],
  regions: ['SUDESTE'],
  showStateInput: false,
  tempStateNames: '',
  applyToSameState: false,
  substituicaoTributaria: false
};

const LabelWithInfo = ({ label, tooltip }: { label: string, tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

const CreateTaxScenario = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = new URLSearchParams(location.search).get('editId');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [ncmOptions, setNcmOptions] = useState<{ id: number; name: string }[]>([]);
  const [selectedNcms, setSelectedNcms] = useState<{ id: number; name: string }[]>([]);
  const [ncmInput, setNcmInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [cstPIS_COFINS, setCstPIS_COFINS] = useState('01');
  const [aliquotaPIS, setAliquotaPIS] = useState(0.65);
  const [aliquotaCOFINS, setAliquotaCOFINS] = useState(3.00);
  const [cstIPI, setCstIPI] = useState('00');
  const [aliquotaIPI, setAliquotaIPI] = useState(0.00);
  const [scenarioName, setScenarioName] = useState('Venda Interestadual Padrão');
  const { setHandleChildSubmit } = useOutletContext<LayoutContext>();
  const [cfops, setCfops] = useState<{ id: number; code: string; description: string }[]>([]);
  const [selectedCfop, setSelectedCfop] = useState<number | string>('');
  const [isLoadingCfops, setIsLoadingCfops] = useState(false);
  const [cfopSearch, setCfopSearch] = useState("");
  const [isCfopListOpen, setIsCfopListOpen] = useState(false);
  const { setFeedback } = useContext(FeedbackContext);
  const [allStates, setAllStates] = useState<{ id: number; name: string; uf: string }[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCodesData>({ cstIcms: [], csosn: [], cstPisCofins: [], cstIbsCbs: [] });
  const [classTribOptions, setClassTribOptions] = useState<ClassTribOption[]>([]);

  const [scenarios, setScenarios] = useState<TaxScenario[]>([
    { id: Date.now(), ...DEFAULT_SCENARIO }
  ]);

  const updateScenario = (id: number, field: string, value: any) => {
    setScenarios(prev => prev.map(s =>
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const fetchCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    try {
      // CNPJ de origem = empresas emissoras (com CompanyGateway), não toda empresa isMain.
      const response = await ApiService.fiscal.getCompanyGateways();
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      const mapped = (data || []).map((cg: any) => {
        const state = cg.Company?.Address?.Neighborhood?.City?.State ?? null;
        return {
          id: cg.companyId,
          cnpj: cg.Company?.cnpj,
          companyName: cg.Company?.companyName,
          state: state ? { id: state.id, name: state.name } : null,
        };
      });
      setCompanies(mapped);
    } catch (error) {
      console.error("Erro ao buscar empresas emissoras:", error);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, []);

  const fetchNcms = useCallback(async () => {
    try {
      const response = await ApiService.stockLookup.getNcms();
      setNcmOptions(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar NCMs:", error);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchNcms();
  }, [fetchCompanies, fetchNcms]);

  const addNcm = (ncmName: string) => {
    const found = ncmOptions.find(opt => opt.name === ncmName);
    if (found && !selectedNcms.find(s => s.id === found.id)) {
      setSelectedNcms([...selectedNcms, found]);
      setNcmInput('');
    }
  };

  const removeNcm = (id: number) => {
    setSelectedNcms(selectedNcms.filter(item => item.id !== id));
  };

  const fetchCfops = useCallback(async () => {
    setIsLoadingCfops(true);
    try {
      const response = await ApiService.fiscal.getCfops();
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      setCfops(data || []);
    } catch (error) {
      console.error("Erro ao buscar CFOPs:", error);
    } finally {
      setIsLoadingCfops(false);
    }
  }, []);

  useEffect(() => {
    fetchCfops();
  }, [fetchCfops]);

  // Pré-carga para modo de edição
  useEffect(() => {
    if (!editId) return;
    const loadExisting = async () => {
      try {
        const { data } = await ApiService.fiscal.getTaxConfig(Number(editId));
        setScenarioName(data.nome || '');
        setSelectedCompany(String(data.companyEmissorId || ''));
        setSelectedCfop(data.cfopId || '');
        setCstPIS_COFINS(data.cstPIS_COFINS || '01');
        setAliquotaPIS(Number(data.aliquotaPIS) || 0.65);
        setAliquotaCOFINS(Number(data.aliquotaCOFINS) || 3.00);
        setCstIPI(data.cstIPI || '00');
        setAliquotaIPI(Number(data.aliquotaIPI) || 0.00);
        if (Array.isArray(data.TaxClassifier)) {
          const ncms = data.TaxClassifier
            .filter((c: any) => c.Ncm)
            .map((c: any) => ({ id: c.Ncm.id, name: c.Ncm.ncm || c.Ncm.name || String(c.ncmId) }));
          setSelectedNcms(ncms);
        }
        if (Array.isArray(data.TaxScenario) && data.TaxScenario.length > 0) {
          setScenarios(data.TaxScenario.map((s: any) => ({
            id: s.id,
            cstICMS: s.cstICMS || DEFAULT_SCENARIO.cstICMS,
            aliquotaICMS: Number(s.aliquotaICMS) || DEFAULT_SCENARIO.aliquotaICMS,
            baseDeCalculo: Number(s.baseDeCalculo) || DEFAULT_SCENARIO.baseDeCalculo,
            cstIBS_CBS: s.cstIBS_CBS || DEFAULT_SCENARIO.cstIBS_CBS,
            classificacaoTributaria: s.classificacaoTributaria || '',
            aliquotaCBS: Number(s.aliquotaCBS) || 0,
            aliquotaIBS: Number(s.aliquotaIBS) || 0,
            diferencialAliquota: Boolean(s.diferencialAliquota),
            substituicaoTributaria: Boolean(s.substituicaoTributaria),
            stateIds: (s.TaxScenarioState || []).filter((st: any) => st.stateId).map((st: any) => st.stateId),
            regions: (s.TaxScenarioState || []).filter((st: any) => st.region).map((st: any) => st.region),
            showStateInput: false,
            tempStateNames: '',
            applyToSameState: false,
          })));
        }
      } catch (error) {
        console.error('Erro ao carregar cenário para edição:', error);
      }
    };
    loadExisting();
  }, [editId]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const { data } = await ApiService.companies.getStates();
        setAllStates(data);
      } catch (error) {
        console.error("Erro ao carregar estados", error);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    const loadTaxCodes = async () => {
      try {
        const [codes, classTrib] = await Promise.all([
          ApiService.fiscal.getTaxCodes(),
          ApiService.fiscal.getClassTrib(),
        ]);
        setTaxCodes(codes.data);
        setClassTribOptions(Array.isArray(classTrib.data) ? classTrib.data : []);
      } catch (error) {
        console.error("Erro ao carregar tabelas tributárias", error);
      }
    };
    loadTaxCodes();
  }, []);

  const filteredOptions = ncmOptions.filter(opt =>
    opt.name.toLowerCase().includes(ncmInput.toLowerCase()) &&
    !selectedNcms.find(s => s.id === opt.id)
  );

  const filteredCfops = cfops.filter(opt => {
    const search = cfopSearch.toLowerCase();
    const code = String(opt.code || "").toLowerCase();
    const description = String(opt.description || "").toLowerCase();
    return code.includes(search) || description.includes(search);
  });

  const selectedCfopData = cfops.find(c => Number(c.id) === Number(selectedCfop));

  const handleSubmit = useCallback(async () => {
    try {
      if (!selectedCompany) {
        return setFeedback({ isOpen: true, message: "Selecione uma empresa antes de salvar.", statusOk: false });
      }

      if (!selectedCfop) {
        return setFeedback({ isOpen: true, message: "O campo CFOP é obrigatório.", statusOk: false });
      }

      if (selectedNcms.length === 0) {
        return setFeedback({ isOpen: true, message: "Adicione pelo menos um NCM ao cenário.", statusOk: false });
      }

      const currentCompany = companies.find(c => Number(c.id) === Number(selectedCompany));
      const originStateId = currentCompany?.state?.id || currentCompany?.stateId;

      const payload = {
        nome: scenarioName,
        cfopId: Number(selectedCfop),
        companyEmissorId: Number(selectedCompany),
        cstIPI: cstIPI,
        cstPIS_COFINS: cstPIS_COFINS,
        aliquotaIPI: String(aliquotaIPI.toFixed(2)),
        aliquotaPIS: String(aliquotaPIS.toFixed(2)),
        aliquotaCOFINS: String(aliquotaCOFINS.toFixed(2)),
        ncmIds: selectedNcms.map(n => Number(n.id)),

        scenarios: scenarios.map(s => {
          const finalStateIds = Array.isArray(s.stateIds) ? s.stateIds.map(id => Number(id)) : [];

          if (s.applyToSameState && originStateId) {
            const id = Number(originStateId);
            if (!finalStateIds.includes(id)) {
              finalStateIds.push(id);
            }
          }

          const scenarioObj: any = {
            cstIBS_CBS: s.cstIBS_CBS,
            classificacaoTributaria: s.classificacaoTributaria,
            aliquotaCBS: String(s.aliquotaCBS || "0.00"),
            aliquotaIBS: String(s.aliquotaIBS || "0.00"),
            cstICMS: s.cstICMS,
            aliquotaICMS: String(s.aliquotaICMS.toFixed(2)),
            baseDeCalculo: String(s.baseDeCalculo.toFixed(2)),
            diferencialAliquota: Boolean(s.diferencialAliquota),
            substituicaoTributaria: Boolean(s.substituicaoTributaria),
            stateIds: finalStateIds,
          };

          if (s.regions && s.regions.length > 0) {
            scenarioObj.regions = s.regions;
          }

          return scenarioObj;
        })
      };

      if (editId) {
        const { ncmIds, ...updatePayload } = payload;
        await ApiService.fiscal.updateTaxConfig(Number(editId), {
          ...updatePayload,
          classifierIds: ncmIds,
        });
      } else {
        await ApiService.fiscal.createTaxConfig(payload);
      }

      setFeedback({ isOpen: true, message: `Cenário fiscal ${editId ? 'atualizado' : 'salvo'} com sucesso!`, statusOk: true });

      setTimeout(() => navigate('/panel/invoice/tax-scenario'), 2000);

    } catch (error: any) {
      console.error("ERRO COMPLETO:", error.response?.data);
      const msg = error.response?.data?.message;
      setFeedback({ isOpen: true, message: `Erro ao salvar: ${Array.isArray(msg) ? msg.join(", ") : msg || "Erro interno no servidor."}`, statusOk: false });
    }
  }, [scenarioName, selectedCfop, selectedCompany, companies, selectedNcms, scenarios, cstIPI, cstPIS_COFINS, aliquotaIPI, aliquotaPIS, aliquotaCOFINS, navigate, editId]);

  useEffect(() => {
    if (setHandleChildSubmit) {
      setHandleChildSubmit(() => handleSubmit);
    }
  }, [handleSubmit, setHandleChildSubmit]);

  return (
    <>
      <MainContainer style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* CONFIGURAÇÃO BASE */}
        <StyledCard>
          <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Configuração Base</CardTitle>
          <FormGrid cols={2}>
            <StyledFormGroup>
              <LabelWithInfo label="Nome do cenário" tooltip="Nome interno para identificar este cenário" />
              <input
                type="text"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="Digite o nome do cenário..."
              />
            </StyledFormGroup>

            <StyledFormGroup>
              <LabelWithInfo label="CNPJ de origem" tooltip="CNPJ da sua empresa que emitirá a nota" />
              <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} disabled={isLoadingCompanies}>
                <option value="">{isLoadingCompanies ? 'Carregando empresas...' : 'Selecione uma empresa'}</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.cnpj} — {company.state?.name} — {company.companyName}
                  </option>
                ))}
              </select>
            </StyledFormGroup>

            <StyledFormGroup style={{ position: 'relative' }}>
              <LabelWithInfo label="CFOP" tooltip="Código Fiscal de Operações e Prestações" />

              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Pesquisar CFOP..."
                  autoComplete="off"
                  value={isCfopListOpen ? cfopSearch : (selectedCfopData ? `${selectedCfopData.code} — ${selectedCfopData.description}` : "")}
                  onChange={(e) => {
                    setCfopSearch(e.target.value);
                    setIsCfopListOpen(true);
                  }}
                  onFocus={() => {
                    setCfopSearch("");
                    setIsCfopListOpen(true);
                  }}
                  onBlur={() => setTimeout(() => setIsCfopListOpen(false), 200)}
                  style={{
                    width: '100%', padding: '12px 15px', borderRadius: '25px',
                    border: '1px solid #e0e0e0', background: '#f0f0f0',
                    fontSize: '14px', outline: 'none'
                  }}
                />

                {isCfopListOpen && filteredCfops.length > 0 && (
                  <DropdownList>
                    {filteredCfops.map((cfop) => (
                      <DropdownItem
                        key={cfop.id}
                        onMouseDown={() => {
                          setSelectedCfop(cfop.id);
                          setIsCfopListOpen(false);
                        }}
                      >
                        <strong>{cfop.code}</strong> — {cfop.description}
                      </DropdownItem>
                    ))}
                  </DropdownList>
                )}
              </div>
            </StyledFormGroup>

            <StyledFormGroup>
              <LabelWithInfo label="NCM(s)" tooltip="Nomenclatura Comum do Mercosul aplicável" />
              <div style={{ background: '#ebebeb', padding: '0 15px', borderRadius: '100px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', position: 'relative', height: '40px' }}>
                {selectedNcms.map((ncm) => (
                  <NcmTag key={ncm.id}>
                    {ncm.name} <X size={14} style={{ cursor: 'pointer' }} onClick={() => removeNcm(ncm.id)} />
                  </NcmTag>
                ))}
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    value={ncmInput}
                    placeholder={selectedNcms.length === 0 ? "Adicionar NCM (;)" : ""}
                    onChange={(e) => { setNcmInput(e.target.value); setShowDropdown(true); }}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNcm(ncmInput); } }}
                    style={{ background: 'none', border: 'none', width: '100%', outline: 'none', fontSize: '14px', color: '#666', padding: 0 }}
                  />
                  {showDropdown && filteredOptions.length > 0 && (
                    <DropdownList style={{ width: '200px', top: '32px' }}>
                      {filteredOptions.map((opt) => (
                        <DropdownItem
                          key={opt.id}
                          onClick={() => { addNcm(opt.name); setNcmInput(''); setShowDropdown(false); }}
                        >
                          {opt.name}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  )}
                </div>
              </div>
            </StyledFormGroup>
          </FormGrid>
        </StyledCard>

        {/* IMPOSTOS BASE */}
        <StyledCard>
          <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Impostos Base</CardTitle>
          <FormGrid cols={1}>
            <StyledFormGroup>
              <LabelWithInfo label="CST PIS/COFINS" tooltip="Código de Situação Tributária do PIS e COFINS" />
              <select value={cstPIS_COFINS} onChange={(e) => setCstPIS_COFINS(e.target.value)}>
                {taxCodes.cstPisCofins.map(opt => (
                  <option key={opt.code} value={opt.code}>{opt.code} — {opt.description}</option>
                ))}
              </select>
            </StyledFormGroup>
          </FormGrid>

          <FormGrid cols={2} style={{ marginTop: '20px' }}>
            <StyledFormGroup>
              <LabelWithInfo label="Alíquota PIS%" tooltip="Percentual de PIS" />
              <input type="number" step="0.01" value={aliquotaPIS} onChange={(e) => setAliquotaPIS(Number(e.target.value))} />
            </StyledFormGroup>
            <StyledFormGroup>
              <LabelWithInfo label="Alíquota COFINS%" tooltip="Percentual de COFINS" />
              <input type="number" step="0.01" value={aliquotaCOFINS} onChange={(e) => setAliquotaCOFINS(Number(e.target.value))} />
            </StyledFormGroup>
          </FormGrid>

          <FormGrid cols={1} style={{ marginTop: '20px' }}>
            <StyledFormGroup>
              <LabelWithInfo label="CST IPI" tooltip="Código de Situação Tributária do IPI" />
              <select value={cstIPI} onChange={(e) => setCstIPI(e.target.value)}>
                {CST_IPI_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </StyledFormGroup>
            <StyledFormGroup style={{ marginTop: '20px' }}>
              <LabelWithInfo label="Alíquota IPI%" tooltip="Percentual de IPI" />
              <input type="number" step="0.01" style={{ width: '49%' }} value={aliquotaIPI} onChange={(e) => setAliquotaIPI(Number(e.target.value))} />
            </StyledFormGroup>
          </FormGrid>
        </StyledCard>

        {/* CENÁRIOS DINÂMICOS */}
        <div style={{ marginTop: '10px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#333', marginBottom: '4px' }}>Cenários fiscais</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Defina as regras de ICMS para cada agrupamento de estados</p>
        </div>

        {scenarios.map((scenario, index) => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            index={index}
            updateScenario={updateScenario}
            removeScenario={(id: number) => setScenarios(scenarios.filter(s => s.id !== id))}
            allStates={allStates}
            taxCodes={taxCodes}
            classTribOptions={classTribOptions}
          />
        ))}

        <DashButton
          type="button"
          onClick={() => setScenarios([...scenarios, { id: Date.now(), ...DEFAULT_SCENARIO }])}
        >
          + Cenário
        </DashButton>
      </MainContainer>
    </>
  );
};

export default CreateTaxScenario;
